import { newId } from '@trips/shared/ids';
import type { Attachment } from '@trips/shared/schema';
import { useEffect, useState } from 'react';
import { api, fetchBlob } from '../lib/api';
import { db } from './db';
import { patch, put } from './mutate';
import { getState } from './store';

/**
 * Photos are resized on the device (a large copy and a thumbnail) before upload, so the
 * phone never pushes full-size camera files. Files wait in IndexedDB until they're sent.
 */

const LARGE = 2048;
const THUMB = 480;

type Variant = 'original' | 'large' | 'thumb';

async function exifTakenAt(file: File): Promise<number | null> {
  try {
    const { default: exifr } = await import('exifr/dist/lite.esm.mjs');
    const data = (await exifr.parse(file, { pick: ['DateTimeOriginal', 'CreateDate'] })) as
      | { DateTimeOriginal?: Date; CreateDate?: Date }
      | undefined;
    const d = data?.DateTimeOriginal ?? data?.CreateDate;
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : null;
  } catch {
    return null;
  }
}

async function encode(
  bitmap: ImageBitmap,
  max: number,
  quality: number,
): Promise<{ blob: Blob; width: number; height: number }> {
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);
  let blob = await canvas.convertToBlob({ type: 'image/webp', quality });
  if (blob.type !== 'image/webp')
    blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
  return { blob, width, height };
}

export async function addFiles(
  ownerType: 'trip' | 'item',
  ownerId: string,
  files: File[],
): Promise<number> {
  let added = 0;
  for (const file of files) {
    const id = newId('att');
    const now = Date.now();
    if (file.type.startsWith('image/')) {
      let bitmap: ImageBitmap;
      try {
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      } catch {
        continue; // Formats the browser can't decode are skipped.
      }
      const [takenAt, large, thumb] = await Promise.all([
        exifTakenAt(file),
        encode(bitmap, LARGE, 0.84),
        encode(bitmap, THUMB, 0.76),
      ]);
      bitmap.close();
      await db.blobs.bulkPut([
        { key: `${id}/large`, blob: large.blob, uploaded: false, savedAt: now },
        { key: `${id}/thumb`, blob: thumb.blob, uploaded: false, savedAt: now },
      ]);
      await put('attachments', {
        id,
        ownerType,
        ownerId,
        kind: 'photo',
        mime: large.blob.type,
        fileName: file.name || null,
        bytes: large.blob.size,
        width: large.width,
        height: large.height,
        caption: '',
        takenAt: takenAt ?? (file.lastModified || null),
        variants: [],
      });
      added++;
    } else if (file.type === 'application/pdf') {
      await db.blobs.put({ key: `${id}/original`, blob: file, uploaded: false, savedAt: now });
      await put('attachments', {
        id,
        ownerType,
        ownerId,
        kind: 'pdf',
        mime: 'application/pdf',
        fileName: file.name || null,
        bytes: file.size,
        width: null,
        height: null,
        caption: '',
        takenAt: null,
        variants: [],
      });
      added++;
    }
  }
  void uploadPending();
  return added;
}

let uploading = false;

/** Sends files that haven't reached the server yet, then records which variants exist. */
export async function uploadPending() {
  if (uploading || getState().session !== 'signed-in') return;
  uploading = true;
  try {
    const pending = await db.blobs.filter((b) => !b.uploaded).toArray();
    for (const entry of pending) {
      const [id, variant] = entry.key.split('/') as [string, Variant];
      await api(`/api/files/${id}/${variant}`, {
        method: 'PUT',
        raw: entry.blob,
        contentType: entry.blob.type,
      });
      await db.blobs.update(entry.key, { uploaded: true });
      const att = getState().tables.attachments.get(id);
      if (att && !att.variants.includes(variant)) {
        await patch('attachments', id, { variants: [...att.variants, variant] });
      }
    }
  } catch {
    // Offline or signed out; try again on the next sync.
  } finally {
    uploading = false;
  }
}

const urlCache = new Map<string, string>();

/** Object URL for a file variant: from this device if we have it, else from the server. */
export function useFileUrl(att: Attachment | undefined, variant: Variant): string | null {
  const key = att ? `${att.id}/${variant}` : null;
  const [url, setUrl] = useState<string | null>(key ? (urlCache.get(key) ?? null) : null);
  useEffect(() => {
    if (!key || urlCache.has(key)) {
      if (key) setUrl(urlCache.get(key)!);
      return;
    }
    let alive = true;
    (async () => {
      const local = await db.blobs.get(key);
      let blob = local?.blob;
      if (!blob) {
        try {
          blob = await fetchBlob(`/api/files/${key}`);
          await db.blobs.put({ key, blob, uploaded: true, savedAt: Date.now() });
        } catch {
          return;
        }
      }
      const u = URL.createObjectURL(blob);
      urlCache.set(key, u);
      if (alive) setUrl(u);
    })();
    return () => {
      alive = false;
    };
  }, [key]);
  return url;
}
